import React from 'react';
import { useNavigate } from 'react-router-dom';

const P = (id: number, w = 800, h = 500) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}&h=${h}&dpr=1`;

const SERVICES = [
  {
    icon: '🧠', title: 'Nepali NLP Engine', color: '#0891b2',
    img: P(4578660),
    desc: 'Custom language models for Nepali — tokenization, sentiment analysis, named entity recognition, and text classification tuned to Nepali grammar and cultural context.',
  },
  {
    icon: '💬', title: 'Intelligent Chatbots', color: '#0f4c81',
    img: P(30530412),
    desc: 'Conversational AI assistants that understand and speak Nepali naturally — deploy on web, mobile, or messaging platforms for citizen services or customer support.',
  },
  {
    icon: '📄', title: 'Document Intelligence', color: '#0891b2',
    img: P(8297361),
    desc: 'OCR and data extraction for Nepali printed and handwritten documents — digitize government forms, land records, and contracts at scale.',
  },
  {
    icon: '🔤', title: 'Translation APIs', color: '#0f4c81',
    img: P(135129),
    desc: 'High-accuracy machine translation between Nepali, English, and Hindi — REST API with domain-specific fine-tuning for legal, medical, and government text.',
  },
  {
    icon: '🎙️', title: 'Speech Solutions', color: '#0891b2',
    img: P(8105748),
    desc: 'Nepali text-to-speech and speech-to-text — natural voice synthesis and real-time transcription for IVR systems, accessibility features, and voice-enabled apps.',
  },
  {
    icon: '⚙️', title: 'AI Automation', color: '#0f4c81',
    img: P(3862599),
    desc: 'Automate document routing, data extraction, and approval workflows — integrate AI into your existing systems to eliminate manual processing at volume.',
  },
  {
    icon: '🔍', title: 'Agentic RAG Systems', color: '#0891b2',
    img: P(8386440),
    desc: "AI that doesn't just search — it autonomously queries your policy libraries, databases, and documents, evaluates results, and synthesizes a grounded, cited answer in Nepali.",
  },
  {
    icon: '🤖', title: 'Agentic AI Workflows', color: '#0f4c81',
    img: P(8386437),
    desc: 'Autonomous agents that plan multi-step tasks, call tools, handle exceptions, and complete end-to-end workflows — from citizen application intake to loan processing to compliance reporting.',
  },
];

const SECTORS = [
  { icon: '🏛️', name: 'Government' },
  { icon: '🏦', name: 'Banking & Finance' },
  { icon: '🏥', name: 'Healthcare' },
  { icon: '🌾', name: 'Agriculture' },
  { icon: '🎓', name: 'Education' },
  { icon: '🛒', name: 'Retail & E-commerce' },
];

const WHY_US = [
  {
    icon: '🇳🇵', title: 'Nepali-First',
    desc: 'Models trained on Nepali-specific corpora — not generic multilingual models retrofitted for Nepali. We understand Devanagari script, local dialects, and bureaucratic language.',
  },
  {
    icon: '📋', title: 'Policy Compliant',
    desc: "Built in alignment with Nepal's National AI Policy 2082 — our solutions meet government standards for data privacy, transparency, and ethical AI deployment.",
  },
  {
    icon: '💰', title: 'Affordable for Nepal',
    desc: "Pricing designed for Nepal's market — not Silicon Valley rates. Tiered plans make enterprise-grade AI accessible to local government and private organizations.",
  },
  {
    icon: '🔧', title: 'End-to-End Support',
    desc: 'From research and custom training to deployment and maintenance — we handle the full AI lifecycle so your team can focus on your mission.',
  },
];

export function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="tab-content">
      {/* Hero with background image */}
      <section
        className="civic-hero"
        style={{
          background: `linear-gradient(135deg, rgba(10,24,50,0.93) 0%, rgba(8,58,100,0.88) 50%, rgba(5,72,90,0.85) 100%), url(${P(29848734, 1600, 900)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="civic-hero-inner">
          <div className="civic-hero-badge">Nepal AI Policy 2082 Compliant · Built in Kathmandu</div>
          <h1 className="civic-hero-title">AI & NLP Solutions<br />Built for Nepal</h1>
          <p className="civic-hero-subtitle">
            We help government agencies and businesses harness artificial intelligence —
            from Nepali language processing to end-to-end workflow automation.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1.75rem' }}>
            <button className="hero-cta" onClick={() => navigate('/services')}>
              Explore Services →
            </button>
            <button
              className="hero-cta"
              onClick={() => navigate('/contact')}
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1.5px solid rgba(255,255,255,0.3)' }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="section" style={{ marginBottom: '3rem' }}>
        <div className="civic-stats-grid">
          {[
            { label: 'Nepali-First NLP', sub: 'Deep language expertise', color: '#0891b2' },
            { label: 'Government Ready', sub: 'AI Policy 2082 compliant', color: '#0f4c81' },
            { label: '8 AI Capabilities', sub: 'Including agentic AI & RAG', color: '#0891b2' },
            { label: 'End-to-End', sub: 'Research to deployment', color: '#0f4c81' },
          ].map(item => (
            <div key={item.label} className="stat-card" style={{ borderTop: `3px solid ${item.color}`, textAlign: 'center' }}>
              <div className="stat-label" style={{ color: item.color }}>{item.label}</div>
              <div className="stat-sub">{item.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services grid with images */}
      <section className="section">
        <h2 className="section-title">Our Services</h2>
        <p className="section-subtitle">
          Eight AI capabilities — from Nepali language processing to agentic workflows — deployable individually or as an integrated platform.
        </p>
        <div className="cards-grid">
          {SERVICES.map(s => (
            <div
              key={s.title}
              className="card"
              style={{ padding: 0, overflow: 'hidden', borderTop: 'none', display: 'flex', flexDirection: 'column' }}
            >
              {/* Image banner */}
              <div style={{
                height: 150,
                backgroundImage: `url(${s.img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                flexShrink: 0,
              }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)`,
                  borderBottom: `3px solid ${s.color}`,
                }}/>
                <span style={{ position: 'absolute', bottom: 10, left: 14, fontSize: '1.6rem' }}>{s.icon}</span>
              </div>
              {/* Content */}
              <div style={{ padding: '1.1rem 1.4rem 1.4rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>{s.title}</h3>
                <p style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.6, flex: 1 }}>{s.desc}</p>
                <button
                  onClick={() => navigate('/services')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: s.color, fontWeight: 600, padding: '0.5rem 0 0', fontSize: '0.875rem', textAlign: 'left' }}
                >
                  Learn more →
                </button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button className="hero-cta" onClick={() => navigate('/services')}>See All Services</button>
        </div>
      </section>

      {/* Industries */}
      <section className="section">
        <h2 className="section-title">Industries We Serve</h2>
        <p className="section-subtitle">Specialized AI solutions for Nepal's most impactful sectors.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {SECTORS.map(s => (
            <div
              key={s.name}
              className="card quick-action-card"
              style={{ textAlign: 'center', cursor: 'pointer', padding: '1.5rem 1rem' }}
              onClick={() => navigate('/solutions')}
            >
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{s.icon}</div>
              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>{s.name}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/solutions')}
            style={{ background: 'none', border: '1.5px solid #0891b2', borderRadius: '20px', padding: '0.6rem 1.5rem', color: '#0891b2', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}
          >
            View Industry Solutions →
          </button>
        </div>
      </section>

      {/* Why Uttar Labs */}
      <section className="section">
        <h2 className="section-title">Why Uttar Labs?</h2>
        <div className="cards-grid">
          {WHY_US.map(w => (
            <div key={w.title} className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{w.icon}</div>
              <h3 className="card-title">{w.title}</h3>
              <p className="card-description">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="card" style={{ background: 'linear-gradient(135deg, #0f4c81 0%, #0891b2 100%)', color: 'white', textAlign: 'center' }}>
          <h2 style={{ color: 'white', marginBottom: '0.75rem', fontSize: '1.8rem' }}>
            Ready to bring AI to your organization?
          </h2>
          <p style={{ opacity: 0.9, fontSize: '1.05rem', maxWidth: 560, margin: '0 auto 1.75rem' }}>
            Whether you're a government agency, bank, hospital, or startup — Uttar Labs can design a solution for your specific needs.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/contact')}
              style={{ padding: '0.875rem 2rem', background: 'white', color: '#0f4c81', border: 'none', borderRadius: '25px', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}
            >
              Schedule a Consultation
            </button>
            <button
              onClick={() => navigate('/services')}
              style={{ padding: '0.875rem 2rem', background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)', borderRadius: '25px', fontWeight: 600, cursor: 'pointer', fontSize: '1rem' }}
            >
              View Services
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
